/* tslint:disable */
/* eslint-disable */
/**
 * REAP API
 * Public REST API for REAP (multi-tenant, agentic AI for network operations).  ### Identifier conventions - **id**: UUID — canonical and used for foreign keys and writes. - **slug**: immutable, human-readable handle (kebab-case) used in configs/URLs. Unique per scope (org) or globally (orgs). - **name**: human-friendly display label (mutable). Do **not** use as a reference key.  ## Authentication Overview  This API supports local credentials (password, passkeys), SSO (OIDC/SAML), and email-verified bootstraps for registration/reset/invitations. The design is enumeration-resistant and minimizes clicks for the common case (daily sign-in).  ### Daily sign-ins (no emailTx required) - **Password**: `POST /auth/password/verify` with `{ email, password }` → returns **loginTx**. - **Passkey (email-first)**: `POST /auth/passkeys/assert` with `{ email }` → WebAuthn options + `challengeToken`   → `POST /auth/passkeys/assert/verify` → **loginTx**. - **Usernameless passkey** (optional): `POST /auth/passkeys/assert` with `{ usernameless: true }`.  Exchange **loginTx** at `POST /auth/token`: - If already org-bound (SSO, single org, or server inference) → returns **TokenResponse**. - If not org-bound and multiple orgs exist → returns **200** with `{ nextStep: \"pickOrg\", memberships:[...] }`.   The server **SHOULD filter out ineligible orgs** so the client can re-call `/auth/token` with `orgId`   without additional policy checks. - To **switch orgs** later, call `POST /auth/token` with `grantType:\"refresh\"` and `orgId`.  ### SSO (OIDC/SAML) - IdP flows produce **`oidcTx`** or **`samlTx`**, which you exchange at `POST /auth/token`. - HRD (home realm discovery): `POST /auth/discover/start` always replies **200** with a discriminated   `nextStep`:     - `sso_redirect` (single, safe target) — redirect using `connection.startUrl` (optionally include `login_hint`).     - `picker` (multiple targets) — render an org/connection picker.     - `verify_email` — prove mailbox control before revealing org/connection detail.   *No membership/policy status leaks at Start; the rich, email-proven view is returned by `/auth/discover/verify`.*  Email verification is a common bootstrap across Discovery and Invitations. Invitation Accept returns emailTx plus invited-org context (policy and SSO connections) without enumerating other orgs. Clients should drive UX from these fields and exchange loginTx/oidcTx/samlTx at `/auth/token`.  ### Email-verified bootstraps (keep `emailTx`) - **Register password/passkey**, **reset password**, and **invitation acceptance** require proving email ownership.   After `/auth/discover/verify` (or `/auth/invitations/accept`) you get **`emailTx`** for these operations or to   obtain a limited pre-org session at `/auth/token` (for example, to create the first org).  ### Enumeration resistance & security - Return generic errors for failed sign-in; normalize response timing and apply rate-limits with small jitter. - Avoid revealing user/org status or policy in pre-verification responses. - Use HTTPS; set appropriate cache controls; consider device-bound refresh tokens and short-lived access tokens.  ### Client authentication REAP supports two client auth mechanisms:  - **Bearer JWT** (`Authorization: Bearer <jwt>`): short-lived, user/session tokens returned by `/auth/token`.   Backends validate these locally via signature/claims.  - **API Key** (`X-Api-Key: <secret>`): long-lived, org-scoped secrets intended for automation (CI/CD, headless jobs).   Keys are validated by REAP’s auth layer; they are not JWTs and should not be sent as Bearer tokens.  **Guidance** - Prefer **Bearer JWTs** for interactive UIs and short-lived clients. - Use **API keys** for machine-to-machine integrations; rotate or **PATCH** `expiresAt` to extend, and **DELETE** (revoke) when done. - Assign permissions via **roles**: provide `roleIds` on create/patch; responses include `rolesResolved` for admin UX. - Do not place API keys in URLs or logs. Keys are shown **once** on creation.  ### API key return semantics - The full API key **secret is returned only in the 201 Create response** of `/orgs/{orgId}/api-keys`. - It is **never** returned by GET/PATCH/DELETE. - Clients should display it once and prompt the operator to store it securely (password manager, vault). - For subsequent requests, present the key as `X-Api-Key: <secret>`; do **not** use the Bearer scheme for API keys.  ## Auth policies (overview) - **SSO JIT (just-in-time) provisioning** can be enabled per org: SSO users can sign in without invitations. - **Invite-only SSO** (optional): require an invitation before SSO users can join the org. - **Local accounts** (optional): allow bootstrap/break-glass users without SSO. - **Discover** avoids user enumeration; org memberships and sign-in options are revealed only after email verification. - **Invitations** never expose tokens/links via API; the backend emails the invite link to the recipient. - **Org auth policy** is a **singleton** at `/orgs/{orgId}/auth/policy` that controls JIT, invite-only SSO, and local-account allowances.   The policy is retrieved/updated via **GET/PUT** and versioned with **ETag**.  ## Discovery result (structure) - **Local methods are global**: `DiscoverVerifyResponse.localConnections` lists user-level methods (`passkey`, `password`) available for the verified email — shown **once** in the UI. - **SSO is per-org**: `memberships[].connections` lists **org-scoped** SSO options (`oidc`, `saml`). This array may be **empty** if the org has no SSO. - **Policy hint**: `memberships[].policy.requiresSso` indicates that the org **requires** SSO (local login should not grant access there), even if local methods exist globally.  ## Passwordless authentication (overview) - New-user and invitation flows converge on **email verification** → short-lived **`emailTx`** (no user enumeration). - Clients then use **Passkeys** (WebAuthn/FIDO2) to enroll a credential (attestation) or sign in (assertion). - Successful passkey assertion yields **`loginTx`**, which is exchanged at **`POST /auth/token`** with `grantType=login_tx`. - **Token grants** supported at `/auth/token`:     - **authorization_code** (OIDC) via **`oidcTx`**     - **saml2_bearer** (SAML) via **`ssoTx`**     - **login_tx** (Passkey) via **`loginTx`**     - **refresh_token** (rotation; optional `orgId` switch)     - **email_tx** (bootstrap pre-org; limited to safe endpoints like creating the first org) - **Stateless challenge** option for passkeys: options responses include a signed **`challengeToken`** that the client must echo on verify,   allowing the server to validate the challenge/guardrails **without storing per-challenge state** (short TTL recommended). - **Optional fast-path**: `POST /auth/passkeys/attest/verify?issueLoginTx=true` may return `loginTx` immediately after registration   (policy permitting), otherwise the default flow is attest → assert → token. - **Usernameless (passkey-first) sign-in**: the portal may skip email/Discover and call   `POST /auth/passkeys/assert` with `{ \"usernameless\": true }`. The server returns   `PublicKeyCredentialRequestOptions` with an empty/omitted `allowCredentials`. The browser’s   Conditional UI prompts if a matching passkey exists; on success, the client verifies at   `/auth/passkeys/assert/verify` to obtain `loginTx`, then exchanges at `/auth/token`.  ## API response conventions - **No envelopes**: endpoints return typed bodies directly. - **Resources**: single GET/POST returns the resource object (composed from `ResourceBase` and `OrgScoped` when applicable). - **Operational endpoints** (e.g., auth/token, passkeys) return typed operation results (no `ResourceBase`). - **Lists**: return `{ items: [...], meta: { ...} }`, where each item is a full resource object and `meta` follows **PageMeta**. Responses MAY include `Link` headers (`rel=\"next\"`, `rel=\"prev\"`). When `meta.exactTotal` is omitted clients SHOULD treat totals as unknown and render “more available” messaging rather than fabricating counts.  ### Cache-Control conventions - **Mutable resource detail** (single GET, POST, PATCH) and every response that carries credentials, tokens, or bootstrap data sends `Cache-Control: no-store`. Browsers MUST revalidate before rendering drawers/forms so operators see their latest edits. - **Collection/list endpoints** that back dashboards or tables may use `Cache-Control: private, max-age=15` together with `ETag` and `Link` headers. Clients SHOULD revalidate on manual refresh (If-None-Match) and treat the TTL as an upper bound. - **Streaming/event feeds** and download endpoints for bootstrap bundles always respond with `Cache-Control: no-store`. - If a response omits `Cache-Control`, treat it as non-cacheable.  ### Identity & scoping - Every object is keyed by `id` (UUID). - Common audit fields come from `ResourceBase` (`createdAt`, `updatedAt`, `createdBy`, `updatedBy`). - Multi-tenant scoping builds on `OrgScoped` (adds `orgId`); request bodies never include `orgId`—it comes from the path.  ### Pagination & sorting - Pagination via `limit` (1–200, default 50), `after`, `before`. Servers SHOULD return `Link` headers that mirror `meta.next`/`meta.prev` and include `meta.exactTotal` when a precise count is available. - `meta.exactTotal` is OPTIONAL. When omitted it signals “total unknown”; when the repository already computes a count (e.g., `COUNT(*)`) servers SHOULD populate it. - Sort (when supported) is a comma-separated list; prefix with `-` for descending (for example `-lastSeenAt,mgmtAddress`).  ### Concurrency & caching - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match** (mismatch → **412**). - `GET` supports **If-None-Match** and may return **304 Not Modified**. - Each response includes **`X-Request-Id`** for troubleshooting.  ### Conditional GETs (ETag / If-None-Match) - Servers return **ETag** headers on GETs for cache-friendly revalidation. - Clients can send **If-None-Match** with the last seen ETag:   - If representation is unchanged → **304 Not Modified** (no body).   - If changed → **200 OK** with a new ETag and the latest representation. - Recommended for frequently-polled resources (e.g., devices list, discovery runs, topology graphs).  ### Error model - Structured errors use the `Problem` schema. - Common statuses: **400** (validation), **401/403** (auth/authz), **404** (not found), **409** (conflict), **412** (ETag mismatch), **429** (rate limit).  ### Credentials model (overview) - **Secret vs Credential vs Assignment**   - **Secret**: write-only value + metadata (`type`, `fingerprint`, `last4`). Value is never returned.   - **Credential**: one access **kind** (`cli_ssh`, `snmp_v2c`, `snmp_v3`, `gnmi`, `api_rest`) with kind-specific fields that **reference Secret IDs** (e.g., SSH key, SNMP passwords, API key, mTLS certs).   - **Credential Assignment**: routing rule that applies a **single credential** by scope (org / sites / specific devices) with optional match filters (hostname regex/prefix/suffix, labels, vendor/model).   - Rotate a secret’s value via `POST /value` (use **If-Match** to guard against concurrent rotation). - **Validation**   - On create/update, the server validates that each referenced Secret ID is of the **expected SecretType** for the credential kind (mismatch → 400). - **Resolution order (when multiple assignments match a device)**   1. Lower `priority` first (e.g., 10 wins over 100)   2. Narrower `scope` (device > sites > org)   3. Most recent `updatedAt` - **Consistency**   - Single-resource endpoints return the resource (no envelopes).   - List endpoints return `{ items, meta }` where `meta` follows `PageMeta` and cursors are passed via `limit/after/before`. - **Concurrency & cache**   - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match**.   - `GET` supports **If-None-Match** to return **304 Not Modified** when ETag matches.  ## Devices (key behaviors)  - **States** — `discovered`, `validated`, `unreachable`, `retired`, `merged` (see `DeviceState`). - **Discovery-managed fields** (for example `vendor`, `model`, `osVersion`, `serial`, `hostname`, `firstSeenAt`, `lastSeenAt`) are **read-only** via API. - **User-editable** fields: `displayName`, `labels` (case-insensitive keys; PATCH overwrites the map), `role` (free text, e.g. `access`, `core`, `firewall`, `wireless-ap`), `notes`. - **Create** is allowed (beta/testing). Default list sort: `-lastSeenAt`. - **Retire** via `PATCH` (`state=retired`). **DELETE** is hard-delete (beta convenience). - **Merge** consolidates duplicates; merged-away device IDs subsequently return **404**.  ## Edge connectivity (Connectors & Connector Clusters)  **Connectors** are lightweight agents (AMI/OVA/etc.) that establish outbound, persistent control channels from customer sites to REAP. Connectors register to an **Organization**, can optionally belong to a **Connector Cluster**, and periodically report health, version, and capabilities.  **Connector Clusters** group one or more connectors for redundancy or capacity. Clusters can be attached to **Sites** (N↔M). The API allows make-before-break migrations: a Site can temporarily be served by multiple clusters. Backend scheduling ensures only one active worker set manages a Site at a time. - The attach result includes **multiAttached** to surface Sites now attached to this cluster and at least one other cluster.  **User-data (first-boot)**: each Connector exposes a one-time, short-lived bootstrap bundle for cloud-init. Content negotiation: - `Accept: text/cloud-config` → cloud-init YAML (default) - `Accept: application/json` → JSON wrapper (opaque to clients) - `Accept: application/base64+json` → base64 of the opaque JSON  One-time semantics: redeemed → **409 Conflict**, expired → **410 Gone**. Responses SHOULD include `Cache-Control: no-store`.  **Concurrency & caching**: mutable single-resource endpoints return **ETag** and require **If-Match** on updates/deletes. Reads MAY accept **If-None-Match** and return **304**. Lists use pagination via `{ items, meta }` (see `PageMeta`).  # SSO / IdP Connections REAP supports per-organization **IdP connections** for **OIDC** and **SAML**. - Connections are modeled as a discriminated union (`type: \"oidc\" | \"saml\"`). - Connection **config** holds IdP metadata. Some fields are **readOnly** and computed by REAP (e.g., `redirectUris` for OIDC; `acsUrl`, `spEntityId` for SAML). - **Concurrency:** single-resource GET returns **ETag**; **PATCH**_/_**DELETE** require **If-Match**. - **Deletes** return **204 No Content**. - **SAML IdP-initiated** sign-in can be allowed/blocked by `IdpSamlConfig.allowIdpInitiated` (default **true**). # OIDC client authentication - Confidential clients only: REAP supports client_secret_basic (default) and client_secret_post. - private_key_jwt and public clients are not supported in this version. - PKCE is supported but off by default; enable per connection via `config.enforcePkce`.  ## Local password authentication (optional) - Fits the same **email-first, enumeration-resistant** pattern: verify email → short-lived **`emailTx`**. - Supports:   - **Register** a password after email verification   - **Verify** password to obtain **`loginTx`** (exchange at `/auth/token` with `grantType=loginTx`)   - **Reset** password (email-verified), **Change** password (bearer session) - Client sends the **clear-text password over HTTPS**; the server performs KDF hashing (e.g., **Argon2id** with per-user salt; optional application-level pepper in KMS). - Store the **password hash (and parameters)** in the **regular user table** (not a secrets manager). If you use a **pepper**, store that pepper as a secret. - Error hardening:   - Same **lockout** semantics as passkeys (use **423** with a neutral error body)   - Consistent, non-enumerating responses for all email-bound flows
 *
 * The version of the OpenAPI document: 1.3.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as runtime from '../runtime';
import { AWSCloudWatchLogsIntegrationFromJSON, AWSCloudWatchLogsIntegrationCreateToJSON, AWSCloudWatchLogsIntegrationEnableResponseFromJSON, AWSCloudWatchLogsIntegrationOnboardResponseFromJSON, AWSCloudWatchLogsIntegrationsListResponseFromJSON, AWSFlowLogsIntegrationFromJSON, AWSFlowLogsIntegrationCreateToJSON, AWSFlowLogsIntegrationEnableResponseFromJSON, AWSFlowLogsIntegrationOnboardResponseFromJSON, AWSFlowLogsIntegrationsListResponseFromJSON, AWSFlowLogsResourceOptionsResponseFromJSON, AWSLogDeliveryResponseFromJSON, AzureResourceChangeEventGridRequestInnerToJSON, AzureResourceChangeValidationResponseFromJSON, AzureResourceChangesIntegrationFromJSON, AzureResourceChangesIntegrationCreateToJSON, AzureResourceChangesIntegrationEnableResponseFromJSON, AzureResourceChangesIntegrationOnboardResponseFromJSON, AzureResourceChangesIntegrationsListResponseFromJSON, AzureTelemetryIntegrationFromJSON, AzureTelemetryIntegrationCreateToJSON, AzureTelemetryIntegrationOnboardResponseFromJSON, AzureTelemetryIntegrationsListResponseFromJSON, CloudTrailIntegrationFromJSON, CloudTrailIntegrationCreateToJSON, CloudTrailIntegrationEnableResponseFromJSON, CloudTrailIntegrationOnboardResponseFromJSON, CloudTrailIntegrationsListResponseFromJSON, OrgsOrgIdAzureKindConnectionIdPostRequestToJSON, } from '../models/index';
/**
 *
 */
export class CloudIntegrationsApi extends runtime.BaseAPI {
    /**
     * Public delivery endpoint for customer-managed CloudTrail setup. The request body is intentionally opaque for the initial rollout and currently forwards raw CloudTrail/EventBridge JSON into the AWS adaptor.
     * Ingest AWS CloudTrail event
     */
    orgsOrgIdAwsCloudtrailPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdAwsCloudtrailPost().');
            }
            if (requestParameters['requestBody'] == null) {
                throw new runtime.RequiredError('requestBody', 'Required parameter "requestBody" was null or undefined when calling orgsOrgIdAwsCloudtrailPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/aws/cloudtrail`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['requestBody'],
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Public delivery endpoint for customer-managed CloudTrail setup. The request body is intentionally opaque for the initial rollout and currently forwards raw CloudTrail/EventBridge JSON into the AWS adaptor.
     * Ingest AWS CloudTrail event
     */
    orgsOrgIdAwsCloudtrailPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdAwsCloudtrailPostRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Receives an AWS CloudWatch Logs delivery batch for a REAP organization.
     * Receive AWS CloudWatch Logs batch
     */
    orgsOrgIdAwsCloudwatchLogsPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdAwsCloudwatchLogsPost().');
            }
            if (requestParameters['requestBody'] == null) {
                throw new runtime.RequiredError('requestBody', 'Required parameter "requestBody" was null or undefined when calling orgsOrgIdAwsCloudwatchLogsPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Amz-Firehose-Access-Key"] = yield this.configuration.apiKey("X-Amz-Firehose-Access-Key"); // AWSLogDeliveryAccessKeyAuth authentication
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/aws/cloudwatch-logs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['requestBody'],
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSLogDeliveryResponseFromJSON(jsonValue));
        });
    }
    /**
     * Receives an AWS CloudWatch Logs delivery batch for a REAP organization.
     * Receive AWS CloudWatch Logs batch
     */
    orgsOrgIdAwsCloudwatchLogsPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdAwsCloudwatchLogsPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Receives an AWS Flow Logs delivery batch for a REAP organization.
     * Receive AWS Flow Logs batch
     */
    orgsOrgIdAwsFlowlogsPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdAwsFlowlogsPost().');
            }
            if (requestParameters['requestBody'] == null) {
                throw new runtime.RequiredError('requestBody', 'Required parameter "requestBody" was null or undefined when calling orgsOrgIdAwsFlowlogsPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Amz-Firehose-Access-Key"] = yield this.configuration.apiKey("X-Amz-Firehose-Access-Key"); // AWSLogDeliveryAccessKeyAuth authentication
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/aws/flowlogs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['requestBody'],
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSLogDeliveryResponseFromJSON(jsonValue));
        });
    }
    /**
     * Receives an AWS Flow Logs delivery batch for a REAP organization.
     * Receive AWS Flow Logs batch
     */
    orgsOrgIdAwsFlowlogsPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdAwsFlowlogsPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * An active integration and its exact API key are bound before the batch reaches Azure Adaptor.
     * Receive one Azure Monitor or VNet flow-log batch
     */
    orgsOrgIdAzureKindConnectionIdPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdAzureKindConnectionIdPost().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdAzureKindConnectionIdPost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdAzureKindConnectionIdPost().');
            }
            if (requestParameters['orgsOrgIdAzureKindConnectionIdPostRequest'] == null) {
                throw new runtime.RequiredError('orgsOrgIdAzureKindConnectionIdPostRequest', 'Required parameter "orgsOrgIdAzureKindConnectionIdPostRequest" was null or undefined when calling orgsOrgIdAzureKindConnectionIdPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/azure/{kind}/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: OrgsOrgIdAzureKindConnectionIdPostRequestToJSON(requestParameters['orgsOrgIdAzureKindConnectionIdPostRequest']),
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * An active integration and its exact API key are bound before the batch reaches Azure Adaptor.
     * Receive one Azure Monitor or VNet flow-log batch
     */
    orgsOrgIdAzureKindConnectionIdPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdAzureKindConnectionIdPostRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Public Event Grid webhook for one Azure subscription. The API key is authenticated for the organization before the request is forwarded to Azure Adaptor. A single subscription-validation event receives a synchronous challenge response and is not logged. Supported resource-change notifications are accepted for OTLP delivery.
     * Receive Azure Event Grid resource changes
     */
    orgsOrgIdAzureResourceChangesSubscriptionIdPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdAzureResourceChangesSubscriptionIdPost().');
            }
            if (requestParameters['subscriptionId'] == null) {
                throw new runtime.RequiredError('subscriptionId', 'Required parameter "subscriptionId" was null or undefined when calling orgsOrgIdAzureResourceChangesSubscriptionIdPost().');
            }
            if (requestParameters['azureResourceChangeEventGridRequestInner'] == null) {
                throw new runtime.RequiredError('azureResourceChangeEventGridRequestInner', 'Required parameter "azureResourceChangeEventGridRequestInner" was null or undefined when calling orgsOrgIdAzureResourceChangesSubscriptionIdPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/azure/resource-changes/{subscriptionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"subscriptionId"}}`, encodeURIComponent(String(requestParameters['subscriptionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: requestParameters['azureResourceChangeEventGridRequestInner'].map(AzureResourceChangeEventGridRequestInnerToJSON),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangeValidationResponseFromJSON(jsonValue));
        });
    }
    /**
     * Public Event Grid webhook for one Azure subscription. The API key is authenticated for the organization before the request is forwarded to Azure Adaptor. A single subscription-validation event receives a synchronous challenge response and is not logged. Supported resource-change notifications are accepted for OTLP delivery.
     * Receive Azure Event Grid resource changes
     */
    orgsOrgIdAzureResourceChangesSubscriptionIdPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdAzureResourceChangesSubscriptionIdPostRaw(requestParameters, initOverrides);
            switch (response.raw.status) {
                case 200:
                    return yield response.value();
                case 202:
                    return null;
                default:
                    return yield response.value();
            }
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDelete().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail/{connectionId}/disable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudTrailIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Re-enables a disabled onboarding record and may return a new AWS CloudFormation quick-create URL.
     * Enable AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail/{connectionId}/enable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudTrailIntegrationEnableResponseFromJSON(jsonValue));
        });
    }
    /**
     * Re-enables a disabled onboarding record and may return a new AWS CloudFormation quick-create URL.
     * Enable AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdGet().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailConnectionIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudTrailIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Get AWS CloudTrail integration
     */
    orgsOrgIdIntegrationsAwsCloudtrailConnectionIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List CloudTrail onboarding records configured for this organization.
     * List AWS CloudTrail integrations
     */
    orgsOrgIdIntegrationsAwsCloudtrailGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudTrailIntegrationsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List CloudTrail onboarding records configured for this organization.
     * List AWS CloudTrail integrations
     */
    orgsOrgIdIntegrationsAwsCloudtrailGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudtrailGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates the REAP-side CloudTrail onboarding record and returns the AWS CloudFormation quick-create URL generated by REAP. Delivery credentials and template details are prepared by REAP and are not returned to the client.
     * Onboard AWS CloudTrail
     */
    orgsOrgIdIntegrationsAwsCloudtrailPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailPost().');
            }
            if (requestParameters['cloudTrailIntegrationCreate'] == null) {
                throw new runtime.RequiredError('cloudTrailIntegrationCreate', 'Required parameter "cloudTrailIntegrationCreate" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudtrailPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudtrail`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CloudTrailIntegrationCreateToJSON(requestParameters['cloudTrailIntegrationCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CloudTrailIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Creates the REAP-side CloudTrail onboarding record and returns the AWS CloudFormation quick-create URL generated by REAP. Delivery credentials and template details are prepared by REAP and are not returned to the client.
     * Onboard AWS CloudTrail
     */
    orgsOrgIdIntegrationsAwsCloudtrailPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudtrailPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDelete().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs/{connectionId}/disable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSCloudWatchLogsIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Re-enables a disabled onboarding record.
     * Enable AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs/{connectionId}/enable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSCloudWatchLogsIntegrationEnableResponseFromJSON(jsonValue));
        });
    }
    /**
     * Re-enables a disabled onboarding record.
     * Enable AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdGet().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSCloudWatchLogsIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Get AWS CloudWatch Logs integration
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List AWS CloudWatch Logs onboarding records configured for this organization.
     * List AWS CloudWatch Logs integrations
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSCloudWatchLogsIntegrationsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List AWS CloudWatch Logs onboarding records configured for this organization.
     * List AWS CloudWatch Logs integrations
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates the REAP-side AWS CloudWatch Logs integration for customer-managed log group delivery.
     * Onboard AWS CloudWatch Logs
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsPost().');
            }
            if (requestParameters['aWSCloudWatchLogsIntegrationCreate'] == null) {
                throw new runtime.RequiredError('aWSCloudWatchLogsIntegrationCreate', 'Required parameter "aWSCloudWatchLogsIntegrationCreate" was null or undefined when calling orgsOrgIdIntegrationsAwsCloudwatchLogsPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/cloudwatch-logs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: AWSCloudWatchLogsIntegrationCreateToJSON(requestParameters['aWSCloudWatchLogsIntegrationCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSCloudWatchLogsIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Creates the REAP-side AWS CloudWatch Logs integration for customer-managed log group delivery.
     * Onboard AWS CloudWatch Logs
     */
    orgsOrgIdIntegrationsAwsCloudwatchLogsPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsCloudwatchLogsPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDelete().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Deletes the onboarding record and revokes the active API key.
     * Delete AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs/{connectionId}/disable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Disables the onboarding record.
     * Disable AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Re-enables a disabled onboarding record and may return a new AWS CloudFormation quick-create URL.
     * Enable AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs/{connectionId}/enable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsIntegrationEnableResponseFromJSON(jsonValue));
        });
    }
    /**
     * Re-enables a disabled onboarding record and may return a new AWS CloudFormation quick-create URL.
     * Enable AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdGet().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsConnectionIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Get AWS Flow Logs integration
     */
    orgsOrgIdIntegrationsAwsFlowlogsConnectionIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List AWS Flow Logs onboarding records configured for this organization.
     * List AWS Flow Logs integrations
     */
    orgsOrgIdIntegrationsAwsFlowlogsGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsIntegrationsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List AWS Flow Logs onboarding records configured for this organization.
     * List AWS Flow Logs integrations
     */
    orgsOrgIdIntegrationsAwsFlowlogsGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Validates the selected local VPC and transit gateway resources for a discovered AWS site, creates the REAP-side AWS Flow Logs integration, and returns the AWS CloudFormation quick-create URL generated by REAP. Delivery credentials and template details are prepared by REAP and are not returned to the client.
     * Onboard AWS Flow Logs
     */
    orgsOrgIdIntegrationsAwsFlowlogsPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsPost().');
            }
            if (requestParameters['aWSFlowLogsIntegrationCreate'] == null) {
                throw new runtime.RequiredError('aWSFlowLogsIntegrationCreate', 'Required parameter "aWSFlowLogsIntegrationCreate" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: AWSFlowLogsIntegrationCreateToJSON(requestParameters['aWSFlowLogsIntegrationCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Validates the selected local VPC and transit gateway resources for a discovered AWS site, creates the REAP-side AWS Flow Logs integration, and returns the AWS CloudFormation quick-create URL generated by REAP. Delivery credentials and template details are prepared by REAP and are not returned to the client.
     * Onboard AWS Flow Logs
     */
    orgsOrgIdIntegrationsAwsFlowlogsPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List discovered local VPC and transit gateway resources that can be selected for AWS Flow Logs onboarding.
     * List AWS Flow Logs selectable resources
     */
    orgsOrgIdIntegrationsAwsFlowlogsResourcesGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsResourcesGet().');
            }
            if (requestParameters['siteId'] == null) {
                throw new runtime.RequiredError('siteId', 'Required parameter "siteId" was null or undefined when calling orgsOrgIdIntegrationsAwsFlowlogsResourcesGet().');
            }
            const queryParameters = {};
            if (requestParameters['siteId'] != null) {
                queryParameters['siteId'] = requestParameters['siteId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/aws/flowlogs/resources`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AWSFlowLogsResourceOptionsResponseFromJSON(jsonValue));
        });
    }
    /**
     * List discovered local VPC and transit gateway resources that can be selected for AWS Flow Logs onboarding.
     * List AWS Flow Logs selectable resources
     */
    orgsOrgIdIntegrationsAwsFlowlogsResourcesGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAwsFlowlogsResourcesGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Delete Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDelete().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDelete().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Delete Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsAzureKindConnectionIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Disable Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdDisablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDisablePost().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDisablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdDisablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}/{connectionId}/disable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureTelemetryIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Disable Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdDisablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureKindConnectionIdDisablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Enable Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdEnablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdEnablePost().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdEnablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdEnablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}/{connectionId}/enable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureTelemetryIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Enable Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdEnablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureKindConnectionIdEnablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdGet().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdGet().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindConnectionIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureTelemetryIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Get Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindConnectionIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureKindConnectionIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List Azure Monitor or VNet flow-log integrations
     */
    orgsOrgIdIntegrationsAzureKindGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindGet().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureTelemetryIntegrationsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List Azure Monitor or VNet flow-log integrations
     */
    orgsOrgIdIntegrationsAzureKindGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureKindGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a one-time delivery key for a customer-side managed-identity relay.
     * Create a source-bound Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureKindPost().');
            }
            if (requestParameters['kind'] == null) {
                throw new runtime.RequiredError('kind', 'Required parameter "kind" was null or undefined when calling orgsOrgIdIntegrationsAzureKindPost().');
            }
            if (requestParameters['azureTelemetryIntegrationCreate'] == null) {
                throw new runtime.RequiredError('azureTelemetryIntegrationCreate', 'Required parameter "azureTelemetryIntegrationCreate" was null or undefined when calling orgsOrgIdIntegrationsAzureKindPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/{kind}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"kind"}}`, encodeURIComponent(String(requestParameters['kind']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: AzureTelemetryIntegrationCreateToJSON(requestParameters['azureTelemetryIntegrationCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureTelemetryIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns a one-time delivery key for a customer-side managed-identity relay.
     * Create a source-bound Azure telemetry integration
     */
    orgsOrgIdIntegrationsAzureKindPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureKindPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Delete Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDelete().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Delete Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Disable Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDisablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDisablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDisablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes/{connectionId}/disable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangesIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Disable Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDisablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureResourceChangesConnectionIdDisablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Enable Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdEnablePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdEnablePost().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdEnablePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes/{connectionId}/enable`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangesIntegrationEnableResponseFromJSON(jsonValue));
        });
    }
    /**
     * Enable Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdEnablePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureResourceChangesConnectionIdEnablePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdGet().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesConnectionIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes/{connectionId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangesIntegrationFromJSON(jsonValue));
        });
    }
    /**
     * Get Azure resource-change integration
     */
    orgsOrgIdIntegrationsAzureResourceChangesConnectionIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureResourceChangesConnectionIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List subscription-scoped Azure Event Grid integrations for this organization.
     * List Azure resource-change integrations
     */
    orgsOrgIdIntegrationsAzureResourceChangesGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangesIntegrationsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * List subscription-scoped Azure Event Grid integrations for this organization.
     * List Azure resource-change integrations
     */
    orgsOrgIdIntegrationsAzureResourceChangesGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureResourceChangesGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates one subscription-scoped Event Grid integration and returns a one-time secureString secret with the existing Lighthouse ARM deployment URL.
     * Onboard Azure resource changes
     */
    orgsOrgIdIntegrationsAzureResourceChangesPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesPost().');
            }
            if (requestParameters['azureResourceChangesIntegrationCreate'] == null) {
                throw new runtime.RequiredError('azureResourceChangesIntegrationCreate', 'Required parameter "azureResourceChangesIntegrationCreate" was null or undefined when calling orgsOrgIdIntegrationsAzureResourceChangesPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/integrations/azure/resource-changes`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: AzureResourceChangesIntegrationCreateToJSON(requestParameters['azureResourceChangesIntegrationCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => AzureResourceChangesIntegrationOnboardResponseFromJSON(jsonValue));
        });
    }
    /**
     * Creates one subscription-scoped Event Grid integration and returns a one-time secureString secret with the existing Lighthouse ARM deployment URL.
     * Onboard Azure resource changes
     */
    orgsOrgIdIntegrationsAzureResourceChangesPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdIntegrationsAzureResourceChangesPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
}
